import {
  Badge,
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Grid,
  Layout,
  Row,
  Upload,
  message,
  notification,
  theme,
  Tooltip,
  Input,
  Select,
  Popconfirm,
} from "antd";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import MeetingIntegrationAPI from "../../../restAPI/MeetingIntegrationAPI";
import {
  EditOutlined,
  GoogleOutlined,
  DeleteOutlined,
  VideoCameraOutlined,
  UploadOutlined,
  LockOutlined,
  UnlockOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import CommonSkeletonLoader from "../../../helperComponents/CommonSkeleton";
import useCurrentCompany from "../../../hooks/useCurrentCompany";

const { Header, Content } = Layout;
const { useBreakpoint } = Grid;
const { Dragger } = Upload;

export default function MeetingIntegration() {
  const { companyId, token, userCompany } = useSelector((state) => state.auth);
  const {
    token: { colorBgContainer, borderRadiusLG, colorBorder },
  } = theme.useToken();
  const screens = useBreakpoint();
  const [meetingConfigurationsForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isZoom, setIsZoom] = useState(false);
  const [isGoogle, setIsGoogle] = useState(false);
  const [isTeams, setIsTeams] = useState(false);
  const [platform, setPlatform] = useState('meeting');
  const [selectedConfigType, setSelectedConfigType] = useState(null)
  const [googleMeetConfigData, setGoogleMeetConfigData] = useState([]);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [zoomConfigData, setZoomConfigData] = useState([]);
  const [teamsConfigData, setTeamsConfigData] = useState([]);
  const [screenLoading, setScreenLoading] = useState(false);
  const [googleMeetPayload, setGoogleMeetPayload] = useState({
    platform: "",
    JSONServiceKeyFile: "",
    status: "",
  });
  const [zoomPayload, setZoomPayload] = useState({
    platform: "",
    zoomClientId: "",
    zoomClientSecret: "",
    zoomAccountId: "",
    status: "",
  });
  const [temasPayload, setTeamsPayload] = useState({
    clientId: "",
    clientSecret: "",
    tenantId: "",
    clientEmail: "",
  });

  const props = {
    name: "file",
    multiple: false,
    beforeUpload: (file) => {
      setGoogleMeetPayload({ JSONServiceKeyFile: file });
      return false;
    },
  };

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement,
    });
  };

  useEffect(() => {
    if(!userCompany) return;
    const companyInfo = userCompany.companyData.find(
      (company) => company.companyId === companyId
    );
    setScreenLoading(true);
    getCompanyConfig(1);
  }, [companyId]);

  const getCompanyConfig = async (
    value,
    integrationType = null,
    statusType = false,
    actionType = null
  ) => {
    await MeetingIntegrationAPI.getCompanyConfig(companyId, token)
      .then((res) => {
        if (res?.data) {
          setGoogleMeetConfigData(res.data.googleMeetConfig);
          setZoomConfigData(res.data.zoomConfig);
          setTeamsConfigData(res.data.msTeamsConfig)
          if (value === 2 && integrationType && actionType) {
            openNotificationWithIcon(
              "success",
              "Successful",
              `${
                actionType === "edit"
                  ? `${
                      integrationType === "zoom" ? "Zoom" : integrationType === "teams" ? "Teams" : "Google"
                    } integration ${
                      statusType ? "inactivate" : "reactivate"
                    } successfully.`
                  : "Meeting integration deleted successfully."
              }`,
              "bottomLeft"
            );
          }
        }
      })
      .catch((err) => {
        console.log("error getting company config data:", err.message);
      }).finally(() => {
        setScreenLoading(false);
      })
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setSaveButtonLoading(false);
    setOpen(false);
    setPlatform('meeting');
    setIsZoom(false);
    setIsGoogle(false);
    setIsTeams(false);
    setSelectedConfigType(null)
    meetingConfigurationsForm.resetFields();
  };

  const platformChange = (value) => {
    setPlatform(value);
    setSelectedConfigType(value)
    if (value === "Google Meet") {
      setIsGoogle(true);
      setIsZoom(false);
      setIsTeams(false)
    } else if (value === "Zoom") {
      setIsGoogle(false);
      setIsZoom(true);
      setIsTeams(false)
    } else if(value === 'Teams'){
      setIsTeams(true)
      setIsZoom(false);
      setIsGoogle(false);
    }
  };

  const handleSaveChange = async () => {
    try {
      await meetingConfigurationsForm.validateFields()
      setSaveButtonLoading(true);
      if (isZoom) {
        const formData = {
          clientId: zoomPayload.zoomClientId,
          clientSecret: zoomPayload.zoomClientSecret,
          accountId: zoomPayload.zoomAccountId,
        };

        await MeetingIntegrationAPI.getZoomAccount(formData, companyId, token)
          .then((res) => {
            if (res?.data) {
              const changeContent = {
                zoomConfig: {
                  displayName: res.data.displayName,
                  email: res.data.email,
                  userPlan: res.data.userPlan,
                  credentials: res.data.credentials,
                },
              };
              updateCompanySettings(changeContent);
            }
            setSaveButtonLoading(false);
          })
          .catch((err) => {
            setSaveButtonLoading(false);
            if (err.response) {
              openNotificationWithIcon(
                "error",
                "Zoom meeting information creation failed",
                `${err.response.data.message}`,
                "bottomLeft"
              );
            } else {
              openNotificationWithIcon(
                "error",
                "Zoom meeting information creation failed",
                `${err.message}`,
                "bottomLeft"
              );
            }
          });
      } else if (isGoogle) {
        const formData = new FormData();
        formData.append(
          "serviceAccountJson",
          googleMeetPayload.JSONServiceKeyFile
        );
        await MeetingIntegrationAPI.getGoogleServiceAccount(
          formData,
          companyId,
          token
        )
          .then((res) => {
            if (res?.data) {
              const changeContent = {
                googleMeetConfig: {
                  serviceAccountName: res.data.serviceAccountName,
                  email: res.data.email,
                  projectId: res.data.projectId,
                  status: true,
                  credentials: res.data.credentials,
                },
              };
              updateCompanySettings(changeContent);
            }
            setSaveButtonLoading(false);
          })
          .catch((err) => {
            setSaveButtonLoading(false);
            if (err.response) {
              openNotificationWithIcon(
                "error",
                "Google Meet information creation failed",
                `${err.response.data.message}`,
                "bottomLeft"
              );
            } else {
              openNotificationWithIcon(
                "error",
                "Google Meet information creation failed",
                `${err.message}`,
                "bottomLeft"
              );
            }
          });
      } else if(isTeams) {
        const payload = {
          clientId: temasPayload.clientId,
          clientSecret: temasPayload.clientSecret,
          tenantId: temasPayload.tenantId,
          clientEmail: temasPayload.clientEmail
        }

        try {
          const response = await MeetingIntegrationAPI.getTeamsAccount(payload, companyId, token);
          if (response.data) {
            const changeContent = {
              // msTeamsConfig: {
              //   clientId: temasPayload.clientId,
              //   clientSecret: temasPayload.clientSecret,
              //   tenantId: temasPayload.tenantId,
              //   clientEmail: temasPayload.clientEmail
              // }
              msTeamsConfig: {
                clientEmail: temasPayload.clientEmail,
                credentials: {
                  clientId: temasPayload.clientId,
                  clientSecret: temasPayload.clientSecret,
                  tenantId: temasPayload.tenantId,
                  clientEmail: temasPayload.clientEmail,
                },
              },
            };
            updateCompanySettings(changeContent);
          } else {
            setSaveButtonLoading(false);
          }
        } catch (error) {
          console.log('error in get teams account :', error)
          setSaveButtonLoading(false);
          if (error.response) {
            openNotificationWithIcon(
              "error",
              "Teams config creation failed",
              `${error.response.data.message}`,
              "bottomLeft"
            );
          } else {
            openNotificationWithIcon(
              "error",
              "Teams config creation failed",
              `${error.message}`,
              "bottomLeft"
            );
          }
        }
      }
      onClose();
    } catch (error) {
      setSaveButtonLoading(false);
      console.log('error in saving details :', error)
    }
  };

  const updateCompanySettings = async (data) => {
    await MeetingIntegrationAPI.updateCompanySettings(data, companyId, token)
      .then((response) => {
        openNotificationWithIcon(
          "success",
          "Successful",
          `${response.message}`,
          "bottomLeft"
        );
        getCompanyConfig(1);
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon(
            "error",
            "Meeting integration failed",
            `${err.response.data.message}`,
            "bottomLeft"
          );
        } else {
          openNotificationWithIcon(
            "error",
            "Meeting integration failed",
            `${err.message}`,
            "bottomLeft"
          );
        }
      });
  };

  const MeetingServices = ({ googleMeetConfig = [], zoomConfig = [], teamsConfigData = [] }) => {
    const { companyId, token, userCompany } = useSelector(
      (state) => state.auth
    );
    const screens = useBreakpoint();
    const {
      token: { colorBgContainer, borderRadiusLG, colorBorder },
    } = theme.useToken();

    const currentCompanyInfo = useCurrentCompany();

    // Combine both configurations for easier filtering
    const allConfigs = [
      ...(googleMeetConfig || []).map((item) => ({ ...item, type: "meet" })),
      ...(zoomConfig || []).map((item) => ({ ...item, type: "zoom" })),
      ...(teamsConfigData || []).map((item) => ({ ...item, type: "teams" })),
    ];

    

    const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (
      type,
      message,
      description,
      placement
    ) => {
      api[type]({
        message,
        description,
        placement,
      });
    };

    // Filter active and inactive services
    const activeServices = allConfigs.filter((item) => item.status);
    const inactiveServices = allConfigs.filter((item) => !item.status);

    const deleteHandler = async (id, type, statusType) => {
      setScreenLoading(true);
      const actionType = "delete";
      try {
        const deleteResponse = await MeetingIntegrationAPI.deleteCompanyConfig(id, companyId, token);
        getCompanyConfig(2, type, statusType, actionType);
      } catch (err) {
        setScreenLoading(false);
        openNotificationWithIcon("error","Meeting information deletion failed",err.response?.data?.message || err.message,"bottomLeft");
      }
    };

    const editHandler = async (item, type, statusType) => {
      setScreenLoading(true);
      const actionType = "edit";
      const statusVal = !item.status;
      const contentChanges = { status: statusVal, platform: item.type };

      try {
        await MeetingIntegrationAPI.updateMeetingStatus(item.id,contentChanges,companyId,token);
        getCompanyConfig(2, type, statusType, actionType);
      } catch (err) {
        setScreenLoading(false);
        openNotificationWithIcon("error","Meeting integration status change failed",err.response?.data?.message || err.message,"bottomLeft");
      }
    };

    const renderMeeting = (item) => {
      const isGoogleMeet = item.type === "meet";
      const isZoomMeet = item.type === "zoom";
      const isTeamsMeet = item.type === "teams";
      const icon = isGoogleMeet ? <GoogleOutlined /> : isZoomMeet ? <VideoCameraOutlined /> : <TeamOutlined />;
      const ribbonText = item.status ? "Active" : "Inactive";
      const ribbonColor = item.status ? "green" : "red";

      return (
        <Row key={item._id}>
          <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
            <Badge.Ribbon text={ribbonText} color={ribbonColor}>
              <div
                style={{
                  marginBottom: 8,
                  padding: "24px 24px",
                  background: colorBgContainer,
                  borderRadius: borderRadiusLG,
                  border: `1px solid ${colorBorder}`,
                }}
              >
                <Flex justify="space-between" align="center" gap={16}>
                  <Flex justify="start" align="center" gap={16}>
                    <div style={{ minWidth: 48 }}>
                      <Button type="primary" size="large" icon={icon} />
                    </div>
                    <Content>
                      <p>
                        {isGoogleMeet
                          ? "Google Configurations"
                          : isZoomMeet ? "Zoom Configurations" : "Teams Configurations"}
                      </p>
                    </Content>
                  </Flex>
                  <Flex align="center" justify="flex-end" gap={20}>
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        onClick={() =>
                          deleteHandler(item.id, item.type, item.status)
                        }
                        icon={<DeleteOutlined />}
                        disabled={item.status}
                      />
                    </Tooltip>
                    <Tooltip title={ribbonText}>
                      <Popconfirm
                        // title="Select meeting state change option"
                        title={
                          item.type === "meet"
                            ? `Do you want to ${
                                item.status ? "inactivate" : "reactivate"
                              } the Google Meet integration?`
                            : item.type === "zoom" ? `Do you want to ${
                                item.status ? "inactivate" : "reactivate"
                              } the Zoom integration?`
                            : `Do you want to ${
                                item.status ? "inactivate" : "reactivate"
                              } the Teams integration?`
                        }
                        onConfirm={() => {
                          editHandler(item, item.type, item.status);
                        }}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ type: "default" }}
                        onCancel={() => openNotificationWithIcon('error', 'Abort', `Status update cancelled`, 'bottomLeft')}
                      >
                        <Button
                          type="text"
                          icon={
                            item.status ? <UnlockOutlined /> : <LockOutlined />
                          }
                        />
                      </Popconfirm>
                    </Tooltip>
                  </Flex>
                </Flex>
              </div>
            </Badge.Ribbon>
          </Col>
        </Row>
      );
    };

    return (
      <>
        {activeServices.map(renderMeeting)}
        {inactiveServices.map(renderMeeting)}
        {contextHolder}
      </>
    );
  };

  return (
    <Layout>
      <div
        style={{
          margin: "16px",
          padding: "8px 12px",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Flex justify="space-between" align="center">
          <Flex justify="flex-start" align="center" gap={16}>
            <span>Meeting Integrations</span>
          </Flex>
          <Button type="primary" onClick={showDrawer}>
            Add
          </Button>
        </Flex>
      </div>
      {
        screenLoading ? <CommonSkeletonLoader itemCount={3} /> : (
          <Content
            style={{
              margin: "16px",
              // padding: '24px 12px',
              minHeight: "70svh",
              // background: colorBgContainer,
              // borderRadius: borderRadiusLG,
            }}
          >
            <MeetingServices
              googleMeetConfig={googleMeetConfigData}
              zoomConfig={zoomConfigData}
              teamsConfigData={teamsConfigData}
            />
            {contextHolder}

            <Drawer
              title={`Add ${platform?.toLowerCase()} configuration`}
              placement="right"
              closable={true}
              onClose={onClose}
              open={open}
              bodyStyle={{ paddingBottom: 80 }}
              footer={[
                <Flex justify="flex-end" align="center" gap="large">
                  <Button onClick={onClose}>
                      Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleSaveChange()}
                    disabled={!selectedConfigType}
                    loading={saveButtonLoading}
                  >
                    Save
                  </Button>
                </Flex>
              ]}
            >
              <Form
                form={meetingConfigurationsForm}
                layout="vertical"
                // onFinish={handleSaveChange}
              >
                <Form.Item label="Select Platform">
                  <Select
                    onChange={platformChange}
                    placeholder="Select Platform"
                    style={{ width: "100%" }}
                    value={selectedConfigType}
                  >
                    <Select.Option value="Google Meet">Google Meet</Select.Option>
                    <Select.Option value="Zoom">Zoom</Select.Option>
                    <Select.Option value="Teams">Teams</Select.Option>
                  </Select>
                </Form.Item>

                {isGoogle && (
                  <Form.Item label="Upload JSON Service Key File">
                    <Dragger {...props}>
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                      <p className="ant-upload-hint">
                        Support for a single upload. Strictly prohibit from
                        uploading company data or other band files.
                      </p>
                    </Dragger>
                  </Form.Item>
                )}

                {isZoom && (
                  <>
                    <Form.Item
                      name="zoomClientId"
                      label="Zoom Client ID"
                      value={zoomPayload["zoomClientId"]}
                      onChange={(e) => {
                        setZoomPayload((prev) => ({
                          ...prev,
                          zoomClientId: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Zoom client ID is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="zoomClientSecret"
                      label="Zoom Client Secret"
                      value={zoomPayload["zoomClientSecret"]}
                      onChange={(e) => {
                        setZoomPayload((prev) => ({
                          ...prev,
                          zoomClientSecret: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Zoom client secret is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="zoomAccountId"
                      label="Zoom Account ID"
                      value={zoomPayload["zoomAccountId"]}
                      onChange={(e) => {
                        setZoomPayload((prev) => ({
                          ...prev,
                          zoomAccountId: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Zoom account ID is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </>
                )}
                {isTeams && (
                  <>
                    <Form.Item
                      name="teamsClientId"
                      label="Teams Client ID"
                      value={temasPayload["clientId"]}
                      onChange={(e) => {
                        setTeamsPayload((prev) => ({
                          ...prev,
                          clientId: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Teams Client ID is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="teamsClientSecret"
                      label="Teams Client Secret"
                      value={temasPayload["clientSecret"]}
                      onChange={(e) => {
                        setTeamsPayload((prev) => ({
                          ...prev,
                          clientSecret: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Teams Client Secret is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="teamsClientEmail"
                      label="Teams Client Email"
                      value={temasPayload["clientEmail"]}
                      onChange={(e) => {
                        setTeamsPayload((prev) => ({
                          ...prev,
                          clientEmail: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Teams Client Email is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="teamsTenantId"
                      label="Teams Tenant ID"
                      value={temasPayload["tenantId"]}
                      onChange={(e) => {
                        setTeamsPayload((prev) => ({
                          ...prev,
                          tenantId: e.target.value,
                        }));
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Teams Tenant ID is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </>
                )}
              </Form>
            </Drawer>
          </Content>
        )
      }
    </Layout>
  );
}
